import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, Mail, Lock, User, Briefcase, AlertTriangle, ArrowLeft, ArrowRight,
  Check, X, Eye, EyeOff, Shield, Users, Crown, Phone, MapPin, Hash,
  Globe, FileText, Loader2, RefreshCw, Sparkles, Clock, CreditCard, ArrowRight as ArrowRightIcon,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import AgreementModal from '../components/AgreementModal';
import LoadingScreen from '../components/ui/LoadingScreen';
import {
  showError,
  showLoading,
  showSuccess,
  updateToError,
  updateToSuccess,
} from '../components/Alerts';
import { tenantService } from '../services/tenantService';
import {
  CompanyNameStatusResponse,
  CompanyNameStatusType,
  ConfirmRegistrationRequest,
  EmailStatusResponse,
  EmailStatusType,
  TaxIdStatusResponse,
  TaxIdStatusType,
} from '../types';
import { formatCuit, isValidCuit, normalizeCuitDigits } from '../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = ['Información', 'Cuenta', 'Empresa', 'Términos', 'Verificación'] as const;

const RUBROS = [
  'Constructora / Obra civil',
  'Arquitectura y diseño',
  'Ingeniería civil',
  'Instalaciones (electricidad / plomería)',
  'Proveedores de materiales',
  'Desarrollo inmobiliario',
  'Administración de propiedades',
  'Otro',
];

const PAISES = ['Argentina'];

const PROVINCIAS_AR = [
  'Buenos Aires',
  'Ciudad Autónoma de Buenos Aires',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego, Antártida e Islas del Atlántico Sur',
  'Tucumán',
];

const EMPLEADOS_RANGOS = ['1–5', '6–15', '16–50', '51–200', '200+'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isValidEmail = (value: string) => /\S+@\S+\.\S+/.test(value);

/** Normalize whitespace: trim + collapse internal spaces */
const collapseSpaces = (value: string) => value.trim().replace(/\s+/g, ' ');

/** Title case for personal names */
const toTitleCase = (value: string) =>
  collapseSpaces(value).replace(/\b\w/g, l => l.toUpperCase());

const getErrorMessage = (error: unknown): string => {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    (error as { response?: { data?: { message?: string } } }).response?.data?.message
  ) {
    return (error as { response: { data: { message: string } } }).response.data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'No se pudo completar el registro. Intentá nuevamente.';
};

/** User-friendly error for async status checks */
const normalizeStatusError = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 400 || status === 422) return 'No se pudo validar. Revisá el valor ingresado.';
  if (status && status >= 500) return 'Error del servidor. Intentá de nuevo en unos momentos.';
  if (error instanceof Error && /network|fetch/i.test(error.message))
    return 'Sin conexión. Verificá tu red e intentá de nuevo.';
  return 'No se pudo verificar la disponibilidad. Intentá de nuevo.';
};

// ─── Password strength ────────────────────────────────────────────────────────

function getPasswordScore(pw: string): number {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)           score++;
  if (pw.length >= 12)          score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw))  score++;
  return score;
}

const STRENGTH_LABELS = ['', 'Muy débil', 'Débil', 'Regular', 'Fuerte', 'Muy fuerte'];
const STRENGTH_BAR    = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-emerald-500'];
const STRENGTH_TEXT   = ['', 'text-red-600 dark:text-red-400', 'text-orange-600 dark:text-orange-400', 'text-yellow-600 dark:text-yellow-400', 'text-blue-600 dark:text-blue-400', 'text-emerald-600 dark:text-emerald-400'];

// ─── Stepper ──────────────────────────────────────────────────────────────────

function Stepper({ step }: { step: number }) {
  return (
    <nav aria-label="Progreso del registro">
      <ol className="flex items-center">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <li className="flex flex-col items-center min-w-0 gap-1">
              <div
                aria-current={i === step ? 'step' : undefined}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
                  ${i < step   ? 'bg-indigo-600 text-white' : ''}
                  ${i === step ? 'bg-indigo-600 text-white ring-4 ring-indigo-500/25' : ''}
                  ${i > step   ? 'bg-muted text-muted-foreground' : ''}
                `}
              >
                {i < step ? <Check className="w-4 h-4" aria-hidden="true" /> : <span>{i + 1}</span>}
              </div>
              <span
                className={`text-[10px] font-medium hidden sm:block whitespace-nowrap
                  ${i <= step ? 'text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}
                `}
              >
                {label}
              </span>
            </li>
            {i < STEPS.length - 1 && (
              <div
                aria-hidden="true"
                className={`flex-1 h-0.5 mx-2 mb-4 sm:mb-0 transition-all duration-500
                  ${i < step ? 'bg-indigo-600' : 'bg-border'}
                `}
              />
            )}
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
}

// ─── Field wrapper ────────────────────────────────────────────────────────────

function Field({
  label, children, required, id,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  id?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
        {required && <span className="sr-only">(requerido)</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Error inline block ───────────────────────────────────────────────────────

function ErrorBlock({ message, onRetry, id }: { message: string; onRetry?: () => void; id?: string }) {
  return (
    <div
      id={id}
      role="alert"
      className="flex items-start gap-2 px-3 py-2 border border-red-200 rounded-lg bg-red-50 dark:bg-red-950/30 dark:border-red-800"
    >
      <X className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className={onRetry ? 'space-y-1.5' : undefined}>
        <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
        {onRetry && (
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            Reintentar
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface AccountData {
  nombre: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber?: string;
  profilePictureUrl?: string;
  bio?: string;
}

interface CompanyData {
  nombreEmpresa: string;
  razonSocial: string;
  cuit: string;
  rubro: string;
  direccion: string;
  pais: string;
  provincia: string;
  ciudad: string;
  empleados: string;
  telefono: string;
  emailEmpresa: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // ── Step navigation
  const [step, setStep]       = useState(0);
  const [visible, setVisible] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [refreshEmailCheckToken, setRefreshEmailCheckToken] = useState(0);
  const [refreshCompanyNameCheckToken, setRefreshCompanyNameCheckToken] = useState(0);
  const [refreshTaxIdCheckToken, setRefreshTaxIdCheckToken] = useState(0);
  const lastCompanyNameToastKey = useRef<string | null>(null);
  const lastTaxIdToastKey = useRef<string | null>(null);

  const goTo = (next: number) => {
    setVisible(false);
    setTimeout(() => {
      setStep(next);
      setVisible(true);
      if (scrollAreaRef.current) scrollAreaRef.current.scrollTop = 0;
    }, 180);
  };

  // ── Step 0
  const [infoChecked, setInfoChecked] = useState(false);

  // ── Step 1 — Cuenta
  const [account, setAccount] = useState<AccountData>({
    nombre: '', email: '', password: '', confirmPassword: '',
    phoneNumber: '', profilePictureUrl: '', bio: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [emailStatusData, setEmailStatusData] = useState<EmailStatusResponse | null>(null);
  const [emailStatusLoading, setEmailStatusLoading] = useState(false);
  const [emailStatusError, setEmailStatusError] = useState<string | null>(null);

  const pwScore = getPasswordScore(account.password);
  const pwChecks = {
    length:    account.password.length >= 8,
    uppercase: /[A-Z]/.test(account.password),
    number:    /[0-9]/.test(account.password),
    special:   /[^A-Za-z0-9]/.test(account.password),
    match:     account.password.length > 0 && account.password === account.confirmPassword,
  };

  useEffect(() => {
    const normalizedEmail = account.email.trim().toLowerCase();
    if (step !== 1 || !isValidEmail(normalizedEmail)) {
      setEmailStatusData(null);
      setEmailStatusLoading(false);
      setEmailStatusError(null);
      return;
    }
    setEmailStatusLoading(true);
    setEmailStatusError(null);
    let cancelled = false;
    const id = window.setTimeout(async () => {
      try {
        const data = await tenantService.getEmailStatus(normalizedEmail);
        if (!cancelled) setEmailStatusData(data);
      } catch (error) {
        if (!cancelled) { setEmailStatusData(null); setEmailStatusError(normalizeStatusError(error)); }
      } finally {
        if (!cancelled) setEmailStatusLoading(false);
      }
    }, 500);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [account.email, step, refreshEmailCheckToken]);

  // ── Step 2 — Empresa
  const [company, setCompany] = useState<CompanyData>({
    nombreEmpresa: '', razonSocial: '', cuit: '', rubro: '',
    direccion: '', pais: 'Argentina', provincia: '', ciudad: '', empleados: '',
    telefono: '', emailEmpresa: '',
  });
  const [companyNameStatusData, setCompanyNameStatusData] = useState<CompanyNameStatusResponse | null>(null);
  const [companyNameStatusLoading, setCompanyNameStatusLoading] = useState(false);
  const [companyNameStatusError, setCompanyNameStatusError] = useState<string | null>(null);

  const [taxIdStatusData, setTaxIdStatusData] = useState<TaxIdStatusResponse | null>(null);
  const [taxIdStatusLoading, setTaxIdStatusLoading] = useState(false);
  const [taxIdStatusError, setTaxIdStatusError] = useState<string | null>(null);
  const [taxIdLocalError, setTaxIdLocalError] = useState<string | null>(null);

  useEffect(() => {
    const normalizedName = company.nombreEmpresa.trim().toLowerCase();
    if (step !== 2 || normalizedName.length < 3) {
      setCompanyNameStatusData(null);
      setCompanyNameStatusLoading(false);
      setCompanyNameStatusError(null);
      return;
    }
    setCompanyNameStatusLoading(true);
    setCompanyNameStatusError(null);
    let cancelled = false;
    const id = window.setTimeout(async () => {
      try {
        const data = await tenantService.getCompanyNameStatus(normalizedName);
        if (!cancelled) setCompanyNameStatusData(data);
      } catch (error) {
        if (!cancelled) { setCompanyNameStatusData(null); setCompanyNameStatusError(normalizeStatusError(error)); }
      } finally {
        if (!cancelled) setCompanyNameStatusLoading(false);
      }
    }, 500);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [company.nombreEmpresa, step, refreshCompanyNameCheckToken]);

  useEffect(() => {
    const digits = normalizeCuitDigits(company.cuit);

    if (step !== 2 || digits.length === 0) {
      setTaxIdStatusData(null); setTaxIdStatusLoading(false);
      setTaxIdStatusError(null); setTaxIdLocalError(null);
      return;
    }

    // No mostrar error mientras el usuario aún está ingresando dígitos
    if (digits.length < 11) {
      setTaxIdStatusData(null); setTaxIdStatusLoading(false);
      setTaxIdStatusError(null); setTaxIdLocalError(null);
      return;
    }

    if (!isValidCuit(digits)) {
      setTaxIdStatusData(null); setTaxIdStatusLoading(false);
      setTaxIdStatusError(null);
      setTaxIdLocalError('CUIT inválido. Verificá los 11 dígitos y el dígito verificador.');
      return;
    }

    setTaxIdLocalError(null);
    setTaxIdStatusLoading(true);
    setTaxIdStatusError(null);
    let cancelled = false;
    const id = window.setTimeout(async () => {
      try {
        const data = await tenantService.getTaxIdStatus(digits);
        if (!cancelled) setTaxIdStatusData(data);
      } catch (error) {
        if (!cancelled) { setTaxIdStatusData(null); setTaxIdStatusError(normalizeStatusError(error)); }
      } finally {
        if (!cancelled) setTaxIdStatusLoading(false);
      }
    }, 500);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [company.cuit, step, refreshTaxIdCheckToken]);

  useEffect(() => {
    if (step !== 2) return;
    const key = company.nombreEmpresa.trim().toLowerCase();
    if (companyNameStatusData?.status === CompanyNameStatusType.ALREADY_EXISTS && key && lastCompanyNameToastKey.current !== key) {
      showError('Ya existe una empresa registrada con este nombre');
      lastCompanyNameToastKey.current = key;
    }
  }, [company.nombreEmpresa, companyNameStatusData, step]);

  useEffect(() => {
    if (step !== 2) return;
    const key = normalizeCuitDigits(company.cuit);
    if (taxIdStatusData?.status === TaxIdStatusType.ALREADY_EXISTS && key && lastTaxIdToastKey.current !== key) {
      showError('Ya existe una empresa registrada con este CUIT');
      lastTaxIdToastKey.current = key;
    }
  }, [company.cuit, taxIdStatusData, step]);

  // ── Step 3 — Términos
  const [termsChecked, setTermsChecked] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);

  // ── Step 4 — OTP
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpResending, setOtpResending] = useState(false);
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);

  const startOtpCooldown = (s = 60) => {
    setOtpResendCooldown(s);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setOtpResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Can advance per step
  const emailCanProceed = useMemo(() => {
    if (!isValidEmail(account.email.trim())) return false;
    if (emailStatusLoading || emailStatusError) return false;
    return emailStatusData?.status === EmailStatusType.AVAILABLE;
  }, [account.email, emailStatusData, emailStatusError, emailStatusLoading]);

  const companyNameCanProceed = useMemo(() => {
    if (company.nombreEmpresa.trim().length < 3) return false;
    if (companyNameStatusLoading || companyNameStatusError) return false;
    return companyNameStatusData?.status === CompanyNameStatusType.AVAILABLE;
  }, [company.nombreEmpresa, companyNameStatusData, companyNameStatusError, companyNameStatusLoading]);

  const taxIdCanProceed = useMemo(() => {
    if (taxIdLocalError || taxIdStatusLoading || taxIdStatusError) return false;
    if (!isValidCuit(company.cuit)) return false;
    return taxIdStatusData?.status === TaxIdStatusType.AVAILABLE;
  }, [company.cuit, taxIdLocalError, taxIdStatusData, taxIdStatusError, taxIdStatusLoading]);

  const canAdvance = [
    infoChecked,
    account.nombre.trim() !== '' &&
      account.email.trim() !== '' &&
      pwScore >= 3 && pwChecks.match && emailCanProceed,
    company.nombreEmpresa.trim() !== '' &&
      company.razonSocial.trim() !== '' &&
      company.cuit.trim() !== '' &&
      companyNameCanProceed && taxIdCanProceed &&
      company.rubro !== '' &&
      company.direccion.trim() !== '' &&
      company.pais !== '' &&
      company.provincia.trim() !== '' &&
      company.ciudad.trim() !== '' &&
      company.empleados !== '',
    termsChecked,
    otpCode.replace(/\D/g, '').length === 6,
  ];

  // ── Status badges — solo se muestran cuando hay contenido relevante
  const getEmailStateBadge = (): React.ReactNode => {
    if (!account.email.trim()) return null;
    if (!isValidEmail(account.email.trim()))
      return <Badge variant="outline">Email inválido</Badge>;
    if (emailStatusLoading)
      return <Badge variant="secondary"><Loader2 className="inline w-3 h-3 mr-1 animate-spin" aria-hidden="true" />Verificando...</Badge>;
    if (emailStatusError)
      return <Badge variant="destructive">No se pudo verificar</Badge>;
    if (emailStatusData?.status === EmailStatusType.AVAILABLE)
      return <Badge className="text-white bg-emerald-600 hover:bg-emerald-600">Disponible</Badge>;
    if (emailStatusData?.status === EmailStatusType.USER_ALREADY_EXISTS)
      return <Badge variant="destructive">Email ya registrado</Badge>;
    if (emailStatusData?.status === EmailStatusType.PENDING_INVITATION)
      return <Badge className="text-white bg-amber-500 hover:bg-amber-500">Invitación pendiente</Badge>;
    return null;
  };

  const getCompanyNameStateBadge = (): React.ReactNode => {
    const len = company.nombreEmpresa.trim().length;
    if (len === 0) return null;
    if (len < 3)
      return <Badge variant="outline">Mín. 3 caracteres</Badge>;
    if (companyNameStatusLoading)
      return <Badge variant="secondary"><Loader2 className="inline w-3 h-3 mr-1 animate-spin" aria-hidden="true" />Verificando...</Badge>;
    if (companyNameStatusError)
      return <Badge variant="destructive">No se pudo verificar</Badge>;
    if (companyNameStatusData?.status === CompanyNameStatusType.AVAILABLE)
      return <Badge className="text-white bg-emerald-600 hover:bg-emerald-600">Disponible</Badge>;
    if (companyNameStatusData?.status === CompanyNameStatusType.ALREADY_EXISTS)
      return <Badge variant="destructive">Ya registrado</Badge>;
    return null;
  };

  const getTaxIdStateBadge = (): React.ReactNode => {
    if (!company.cuit.trim()) return null;
    if (taxIdLocalError)
      return <Badge variant="destructive">CUIT inválido</Badge>;
    if (taxIdStatusLoading)
      return <Badge variant="secondary"><Loader2 className="inline w-3 h-3 mr-1 animate-spin" aria-hidden="true" />Verificando...</Badge>;
    if (taxIdStatusError)
      return <Badge variant="destructive">No se pudo verificar</Badge>;
    if (taxIdStatusData?.status === TaxIdStatusType.AVAILABLE)
      return <Badge className="text-white bg-emerald-600 hover:bg-emerald-600">Disponible</Badge>;
    if (taxIdStatusData?.status === TaxIdStatusType.ALREADY_EXISTS)
      return <Badge variant="destructive">Ya registrado</Badge>;
    if (taxIdStatusData?.status === TaxIdStatusType.INVALID_FORMAT)
      return <Badge variant="destructive">Formato inválido</Badge>;
    return null;
  };

  // Solicita el OTP al pasar del paso Términos (step 3) al paso Verificación (step 4)
  const handleRequestOtp = async () => {
    const loadingToastId = showLoading('Enviando código de verificación...');
    try {
      await tenantService.requestOtp({ email: account.email.trim().toLowerCase() });
      updateToSuccess(loadingToastId, 'Código enviado a ' + account.email.trim().toLowerCase());
      setOtpCode('');
      setOtpError('');
      startOtpCooldown();
      goTo(4);
    } catch {
      updateToError(loadingToastId, 'No se pudo enviar el código. Intentá de nuevo.');
    }
  };

  const handleOtpResend = async () => {
    if (otpResendCooldown > 0 || otpResending) return;
    setOtpResending(true);
    try {
      await tenantService.requestOtp({ email: account.email.trim().toLowerCase() });
      setOtpCode('');
      setOtpError('');
      startOtpCooldown();
      showSuccess('Nuevo código enviado');
    } catch {
      showError('No se pudo reenviar el código.');
    } finally {
      setOtpResending(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting || !canAdvance[4]) return;
    setSubmitting(true);
    const loadingToastId = showLoading('Creando cuenta empresarial...');

    const payload: ConfirmRegistrationRequest = {
      companyName:   company.nombreEmpresa.trim(),
      adminName:     account.nombre.trim(),
      adminEmail:    account.email.trim().toLowerCase(),
      adminPassword: account.password,
      legalName:     company.razonSocial.trim(),
      taxId:         company.cuit.trim(),
      industry:      company.rubro,
      companyEmail:  company.emailEmpresa.trim() || undefined,
      companyPhone:  company.telefono.trim() || undefined,
      address:       company.direccion.trim(),
      country:       company.pais,
      province:      company.provincia.trim(),
      city:          company.ciudad.trim(),
      employeeCount: company.empleados,
      adminPhoneNumber: account.phoneNumber,
      adminProfilePictureUrl: account.profilePictureUrl,
      adminBio: account.bio,
      otpCode,
    };

    try {
      await tenantService.confirmRegistration(payload);
      updateToSuccess(loadingToastId, '¡Empresa registrada! Tu trial de 7 días comienza ahora.');
      setShowTrialModal(true);
    } catch (error) {
      const message = getErrorMessage(error);
      updateToError(loadingToastId, message);
      // Si el error es del OTP, volver al paso de verificación
      if (message.toLowerCase().includes('código') || message.toLowerCase().includes('otp')) {
        setOtpError(message);
        setOtpCode('');
        goTo(4);
      } else {
        showError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Computed badge nodes (avoid calling twice per render)
  const emailBadge         = getEmailStateBadge();
  const companyNameBadge   = getCompanyNameStateBadge();
  const taxIdBadge         = getTaxIdStateBadge();

  // ── Email error state
  const emailHasError = !!emailStatusError ||
    emailStatusData?.status === EmailStatusType.USER_ALREADY_EXISTS ||
    emailStatusData?.status === EmailStatusType.PENDING_INVITATION;

  // ── CUIT error state
  const cuitHasError = !!taxIdLocalError || !!taxIdStatusError ||
    taxIdStatusData?.status === TaxIdStatusType.ALREADY_EXISTS;

  // ── Company name error state
  const companyNameHasError = !!companyNameStatusError ||
    companyNameStatusData?.status === CompanyNameStatusType.ALREADY_EXISTS;

  return (
    <div className="flex h-screen overflow-hidden">

      {/* ── Left panel ───────────────────────────────────────────────────── */}
      <div
        aria-hidden="true"
        className="relative hidden overflow-hidden lg:flex lg:w-2/5 xl:w-1/2 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900"
      >
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(99,102,241,0.25) 0%, transparent 55%),
                            radial-gradient(circle at 75% 75%, rgba(99,102,241,0.15) 0%, transparent 55%)`,
        }} />
        <div className="absolute w-32 h-32 border top-20 left-16 border-indigo-500/20 rounded-2xl rotate-12 animate-pulse" />
        <div className="absolute w-24 h-24 border bottom-32 right-20 border-indigo-400/15 rounded-xl -rotate-12" />
        <div className="absolute w-16 h-16 rotate-45 rounded-lg top-1/2 left-1/3 bg-indigo-500/10" />

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="flex items-center gap-4 mb-10">
            <div className="flex items-center justify-center border w-14 h-14 rounded-2xl bg-indigo-500/20 backdrop-blur-sm border-indigo-500/30">
              <Building2 className="text-indigo-400 w-7 h-7" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-white">PruneStone</h1>
              <p className="text-sm font-medium text-indigo-300/80">Construction Management</p>
            </div>
          </div>
          <h2 className="mb-6 text-4xl font-bold leading-tight text-white xl:text-5xl">
            Creá tu espacio<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
              de trabajo
            </span>
          </h2>
          <p className="max-w-sm mb-10 text-lg leading-relaxed text-slate-400">
            Registrá tu empresa y accedé a un sistema de gestión completo, diseñado para constructoras.
          </p>
          <div className="space-y-3">
            {[
              { icon: Crown,     text: 'Serás el administrador principal' },
              { icon: Users,     text: 'Invitá a tu equipo cuando quieras' },
              { icon: Shield,    text: 'Datos seguros y aislados' },
              { icon: Briefcase, text: 'Diseñado para el rubro constructor' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 border rounded-lg bg-indigo-500/15 border-indigo-500/20">
                  <Icon className="w-4 h-4 text-indigo-400" />
                </div>
                <span className="text-sm text-slate-300">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel ──────────────────────────────────────────────────── */}
      <div
        className="flex flex-col flex-1 overflow-hidden bg-background"
        aria-busy={submitting}
      >
        {/* Header stepper — no scrollea */}
        <div className="z-10 flex-shrink-0 px-6 py-4 border-b bg-background/95 backdrop-blur border-border">
          <div className="flex items-center gap-3 mb-4 lg:hidden">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10">
              <Building2 className="w-4 h-4 text-primary" aria-hidden="true" />
            </div>
            <span className="text-lg font-bold text-foreground">PruneStone</span>
          </div>
          <Stepper step={step} />
        </div>

        {/* Área de contenido — solo aquí scrollea */}
        <div className="flex-1 overflow-y-auto" ref={scrollAreaRef}>
          <div
            className={`transition-[opacity,transform] duration-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            <div className="max-w-2xl px-4 py-8 mx-auto sm:px-6">

              {/* ── STEP 0 ── */}
              {step === 0 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Antes de comenzar</h2>
                    <p className="mt-1 text-muted-foreground">
                      Leé esta información importante sobre el proceso de registro.
                    </p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    {[
                      { icon: Building2, color: 'indigo', title: 'Nuevo espacio de trabajo', desc: 'Al registrarte creás un entorno empresarial completamente nuevo e independiente para tu organización.' },
                      { icon: Crown, color: 'purple', title: 'Serás el administrador', desc: 'Tu cuenta tendrá acceso completo. Podrás configurar módulos, roles y permisos.' },
                      { icon: Users, color: 'emerald', title: 'Invitá a tu equipo', desc: 'Desde el sistema invitás a tu equipo por email. Ellos no deben registrarse aquí.' },
                    ].map(({ icon: Icon, color, title, desc }) => (
                      <div key={title} className="p-4 border rounded-xl border-border bg-card">
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3
                          ${color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : ''}
                          ${color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : ''}
                          ${color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : ''}
                        `}>
                          <Icon className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-foreground">{title}</h3>
                        <p className="text-xs leading-relaxed text-muted-foreground">{desc}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3 p-4 border rounded-xl border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                        ¿Fuiste invitado por una empresa?
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-amber-700 dark:text-amber-400">
                        Si sos empleado o colaborador de una empresa que ya usa PruneStone,{' '}
                        <strong>no debés registrarte aquí</strong>. Buscá el correo de invitación
                        en tu bandeja de entrada y aceptá desde ese enlace.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      id="info-check"
                      role="checkbox"
                      aria-checked={infoChecked}
                      onClick={() => setInfoChecked(v => !v)}
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200
                        ${infoChecked ? 'bg-indigo-600 border-indigo-600' : 'border-border hover:border-indigo-400'}
                      `}
                    >
                      {infoChecked && <Check className="w-3 h-3 text-white" aria-hidden="true" />}
                    </button>
                    <label htmlFor="info-check" className="text-sm leading-relaxed cursor-pointer select-none text-foreground">
                      He leído la información anterior y quiero avanzar con el registro de mi empresa.
                    </label>
                  </div>
                </div>
              )}

              {/* ── STEP 1 — Cuenta ─── */}
              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Tu cuenta de administrador</h2>
                    <p className="mt-1 text-muted-foreground">
                      Creá las credenciales con las que accederás al sistema como propietario.
                    </p>
                  </div>

                  {/* Nombre */}
                  <Field label="Nombre completo" required id="f-nombre">
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                      <Input
                        id="f-nombre"
                        type="text"
                        value={account.nombre}
                        onChange={e => setAccount(p => ({ ...p, nombre: e.target.value }))}
                        onBlur={e => setAccount(p => ({ ...p, nombre: toTitleCase(e.target.value) }))}
                        placeholder="Ej: Juan García"
                        maxLength={100}
                        disabled={submitting}
                        autoComplete="name"
                        className="pl-10 rounded-xl"
                      />
                    </div>
                  </Field>

                  {/* Email */}
                  <Field label="Email personal" required id="f-email">
                    <div className="flex items-center justify-between mb-1.5 min-h-[1.5rem]">
                      <span className="text-xs text-muted-foreground">Este email será tu acceso al sistema.</span>
                      <span aria-live="polite" className="flex-shrink-0">{emailBadge}</span>
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                      <Input
                        id="f-email"
                        type="email"
                        value={account.email}
                        onChange={e => { setAccount(p => ({ ...p, email: e.target.value })); setEmailStatusError(null); }}
                        placeholder="tu@empresa.com"
                        maxLength={150}
                        disabled={submitting}
                        autoComplete="email"
                        aria-invalid={emailHasError || undefined}
                        aria-describedby={emailHasError ? 'f-email-error' : undefined}
                        className="pl-10 rounded-xl"
                      />
                    </div>
                    {emailStatusError && (
                      <ErrorBlock
                        id="f-email-error"
                        message={emailStatusError}
                        onRetry={() => setRefreshEmailCheckToken(t => t + 1)}
                      />
                    )}
                    {emailStatusData?.status === EmailStatusType.USER_ALREADY_EXISTS && (
                      <ErrorBlock id="f-email-error" message="Este correo ya se encuentra registrado en el sistema." />
                    )}
                    {emailStatusData?.status === EmailStatusType.PENDING_INVITATION && (
                      <div id="f-email-error" role="alert" className="flex items-start gap-2 px-3 py-2 border rounded-lg bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <div>
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            Este correo fue invitado por una empresa. Debés aceptar desde el email de invitación.
                          </p>
                          {emailStatusData.tenantName && (
                            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                              Empresa: <strong>{emailStatusData.tenantName}</strong>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </Field>

                  {/* Teléfono */}
                  <Field label="Teléfono" id="f-phone">
                    <Input
                      id="f-phone"
                      type="text"
                      value={account.phoneNumber}
                      onChange={e => setAccount(p => ({ ...p, phoneNumber: e.target.value }))}
                      placeholder="Opcional. Ej: +5491112345678"
                      maxLength={50}
                      disabled={submitting}
                      className="rounded-xl"
                    />
                  </Field>

                  {/* Foto de perfil */}
                  <Field label="URL de foto de perfil" id="f-avatar">
                    <Input
                      id="f-avatar"
                      type="url"
                      value={account.profilePictureUrl}
                      onChange={e => setAccount(p => ({ ...p, profilePictureUrl: e.target.value }))}
                      placeholder="Opcional. Ej: https://ejemplo.com/foto.jpg"
                      maxLength={500}
                      disabled={submitting}
                      className="rounded-xl"
                    />
                  </Field>

                  {/* Bio */}
                  <Field label="Biografía / Notas" id="f-bio">
                    <textarea
                      id="f-bio"
                      value={account.bio}
                      onChange={e => setAccount(p => ({ ...p, bio: e.target.value }))}
                      placeholder="Opcional. Contanos sobre vos..."
                      disabled={submitting}
                      rows={2}
                      className="w-full px-3 py-2 text-sm transition-all duration-200 border resize-none border-input rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-input disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </Field>

                  {/* Contraseña */}
                  <Field label="Contraseña" required id="f-password">
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                      <Input
                        id="f-password"
                        type={showPassword ? 'text' : 'password'}
                        value={account.password}
                        onChange={e => setAccount(p => ({ ...p, password: e.target.value }))}
                        placeholder="Mínimo 8 caracteres"
                        maxLength={128}
                        disabled={submitting}
                        autoComplete="new-password"
                        className="pl-10 pr-10 rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        disabled={submitting}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                      </button>
                    </div>
                    {account.password && (
                      <>
                        <div className="space-y-1.5 mt-2" aria-live="polite">
                          <div className="flex gap-1" aria-hidden="true">
                            {[1, 2, 3, 4, 5].map(i => (
                              <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= pwScore ? STRENGTH_BAR[pwScore] : 'bg-muted'}`} />
                            ))}
                          </div>
                          {pwScore > 0 && (
                            <p className={`text-xs font-medium ${STRENGTH_TEXT[pwScore]}`}>
                              Seguridad: {STRENGTH_LABELS[pwScore]}
                            </p>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-1.5 mt-3" aria-label="Requisitos de contraseña">
                          {[
                            { ok: pwChecks.length,    label: 'Mínimo 8 caracteres' },
                            { ok: pwChecks.uppercase, label: 'Una mayúscula' },
                            { ok: pwChecks.number,    label: 'Un número' },
                            { ok: pwChecks.special,   label: 'Un carácter especial' },
                          ].map(({ ok, label }) => (
                            <div key={label} className="flex items-center gap-1.5">
                              {ok
                                ? <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" aria-hidden="true" />
                                : <X     className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                              }
                              <span className={`text-xs ${ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                                {label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </Field>

                  {/* Confirmar contraseña */}
                  <Field label="Confirmar contraseña" required id="f-confirm">
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                      <Input
                        id="f-confirm"
                        type={showConfirm ? 'text' : 'password'}
                        value={account.confirmPassword}
                        onChange={e => setAccount(p => ({ ...p, confirmPassword: e.target.value }))}
                        placeholder="Repetí tu contraseña"
                        maxLength={128}
                        disabled={submitting}
                        autoComplete="new-password"
                        aria-invalid={(account.confirmPassword.length > 0 && !pwChecks.match) || undefined}
                        className={`pl-10 pr-10 rounded-xl ${
                          account.confirmPassword && !pwChecks.match
                            ? 'border-red-400 focus:ring-red-500/30'
                            : account.confirmPassword && pwChecks.match
                              ? 'border-emerald-400 focus:ring-emerald-500/30'
                              : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(v => !v)}
                        disabled={submitting}
                        aria-label={showConfirm ? 'Ocultar confirmación' : 'Mostrar confirmación'}
                        className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirm ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                      </button>
                    </div>
                    {account.confirmPassword && (
                      <div className="flex items-center gap-1.5" aria-live="polite">
                        {pwChecks.match ? (
                          <>
                            <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" aria-hidden="true" />
                            <span className="text-xs text-emerald-600 dark:text-emerald-400">Las contraseñas coinciden</span>
                          </>
                        ) : (
                          <>
                            <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0" aria-hidden="true" />
                            <span className="text-xs text-red-600 dark:text-red-400">Las contraseñas no coinciden</span>
                          </>
                        )}
                      </div>
                    )}
                  </Field>
                </div>
              )}

              {/* ── STEP 2 — Empresa ─── */}
              {step === 2 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Datos de tu empresa</h2>
                    <p className="mt-1 text-muted-foreground">
                      Esta información identifica tu espacio de trabajo dentro del sistema.
                    </p>
                  </div>

                  {/* Bloque 1: Identidad */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-border">
                      <Building2 className="w-4 h-4 text-primary" aria-hidden="true" />
                      <h3 className="text-sm font-semibold text-foreground">Identidad de la empresa</h3>
                    </div>

                    <Field label="Nombre de la empresa" required id="f-nombre-empresa">
                      <div className="flex items-center justify-between mb-1.5 min-h-[1.5rem]">
                        <span className="text-xs text-muted-foreground">Debe ser único dentro del sistema.</span>
                        <span aria-live="polite" className="flex-shrink-0">{companyNameBadge}</span>
                      </div>
                      <div className="relative">
                        <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                        <Input
                          id="f-nombre-empresa"
                          type="text"
                          value={company.nombreEmpresa}
                          onChange={e => { setCompany(p => ({ ...p, nombreEmpresa: e.target.value })); setCompanyNameStatusError(null); }}
                          onBlur={e => setCompany(p => ({ ...p, nombreEmpresa: collapseSpaces(e.target.value) }))}
                          placeholder="Ej: Constructora Atlas S.A."
                          maxLength={120}
                          disabled={submitting}
                          aria-invalid={companyNameHasError || undefined}
                          aria-describedby={companyNameHasError ? 'f-nombre-empresa-error' : undefined}
                          className="pl-10 rounded-xl"
                        />
                      </div>
                      {companyNameStatusError && (
                        <ErrorBlock id="f-nombre-empresa-error" message={companyNameStatusError} onRetry={() => setRefreshCompanyNameCheckToken(t => t + 1)} />
                      )}
                      {companyNameStatusData?.status === CompanyNameStatusType.ALREADY_EXISTS && (
                        <ErrorBlock id="f-nombre-empresa-error" message="Ya existe una empresa registrada con este nombre." />
                      )}
                    </Field>

                    {/* Razón social + CUIT */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Razón social" required id="f-razon-social">
                        <div className="relative">
                          <Briefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                          <Input
                            id="f-razon-social"
                            type="text"
                            value={company.razonSocial}
                            onChange={e => setCompany(p => ({ ...p, razonSocial: e.target.value }))}
                            onBlur={e => setCompany(p => ({ ...p, razonSocial: collapseSpaces(e.target.value) }))}
                            placeholder="Ej: Atlas Construcciones S.R.L."
                            maxLength={150}
                            disabled={submitting}
                            className="pl-10 rounded-xl"
                          />
                        </div>
                      </Field>

                      <Field label="CUIT" required id="f-cuit">
                        <div className="relative">
                          <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                          <Input
                            id="f-cuit"
                            type="text"
                            inputMode="numeric"
                            value={company.cuit}
                            onChange={e => {
                              const raw = normalizeCuitDigits(e.target.value).slice(0, 11);
                              setCompany(p => ({ ...p, cuit: formatCuit(raw) }));
                              setTaxIdStatusError(null);
                            }}
                            placeholder="30-12345678-1"
                            disabled={submitting}
                            aria-invalid={cuitHasError || undefined}
                            aria-describedby={cuitHasError ? 'f-cuit-error' : undefined}
                            className="pl-10 rounded-xl"
                          />
                        </div>
                        {/* Badge solo cuando hay contenido — no desplaza el campo Razón social */}
                        {taxIdBadge && (
                          <div className="flex justify-end mt-1" aria-live="polite">
                            {taxIdBadge}
                          </div>
                        )}
                        {taxIdLocalError && <ErrorBlock id="f-cuit-error" message={taxIdLocalError} />}
                        {taxIdStatusError && <ErrorBlock id="f-cuit-error" message={taxIdStatusError} onRetry={() => setRefreshTaxIdCheckToken(t => t + 1)} />}
                        {taxIdStatusData?.status === TaxIdStatusType.ALREADY_EXISTS && (
                          <ErrorBlock id="f-cuit-error" message="Ya existe una empresa registrada con este CUIT." />
                        )}
                      </Field>
                    </div>

                    <Field label="Rubro / Tipo de empresa" required id="f-rubro">
                      <div className="relative">
                        <Select
                          value={company.rubro}
                          onValueChange={value => setCompany(p => ({ ...p, rubro: value }))}
                          disabled={submitting}
                        >
                          <SelectTrigger id="f-rubro" className="pl-10 rounded-xl">
                            <SelectValue placeholder="Seleccioná un rubro..." />
                          </SelectTrigger>
                          <Briefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" aria-hidden="true" />
                          <SelectContent>
                            {RUBROS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </Field>
                  </div>

                  {/* Bloque 2: Contacto */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-border">
                      <Mail className="w-4 h-4 text-primary" aria-hidden="true" />
                      <h3 className="text-sm font-semibold text-foreground">Información de contacto</h3>
                      <span className="text-xs text-muted-foreground">(opcional)</span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Email corporativo" id="f-email-empresa">
                        <div className="relative">
                          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                          <Input
                            id="f-email-empresa"
                            type="email"
                            value={company.emailEmpresa}
                            onChange={e => setCompany(p => ({ ...p, emailEmpresa: e.target.value }))}
                            placeholder="contacto@empresa.com"
                            maxLength={150}
                            disabled={submitting}
                            autoComplete="organization email"
                            className="pl-10 rounded-xl"
                          />
                        </div>
                      </Field>
                      <Field label="Teléfono de contacto" id="f-telefono">
                        <div className="relative">
                          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                          <Input
                            id="f-telefono"
                            type="tel"
                            value={company.telefono}
                            onChange={e => setCompany(p => ({ ...p, telefono: e.target.value }))}
                            placeholder="11 1234-5678"
                            maxLength={30}
                            disabled={submitting}
                            autoComplete="tel"
                            className="pl-10 rounded-xl"
                          />
                        </div>
                      </Field>
                    </div>
                  </div>

                  {/* Bloque 3: Ubicación */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-border">
                      <MapPin className="w-4 h-4 text-primary" aria-hidden="true" />
                      <h3 className="text-sm font-semibold text-foreground">Ubicación</h3>
                    </div>

                    <Field label="País" required id="f-pais">
                      <div className="relative">
                        <Select value={company.pais} onValueChange={value => setCompany(p => ({ ...p, pais: value }))} disabled>
                          <SelectTrigger id="f-pais" className="pl-10 cursor-not-allowed rounded-xl opacity-70" disabled>
                            <SelectValue placeholder="Seleccioná un país" />
                          </SelectTrigger>
                          <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" aria-hidden="true" />
                          <SelectContent>
                            {PAISES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Por el momento disponible únicamente para empresas de Argentina.
                      </p>
                    </Field>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Provincia" required id="f-provincia">
                        <div className="relative">
                          <Select
                            value={company.provincia}
                            onValueChange={value => setCompany(p => ({ ...p, provincia: value }))}
                            disabled={submitting}
                          >
                            <SelectTrigger id="f-provincia" className="pl-10 rounded-xl">
                              <SelectValue placeholder="Seleccioná una provincia" />
                            </SelectTrigger>
                            <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" aria-hidden="true" />
                            <SelectContent>
                              {PROVINCIAS_AR.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </Field>

                      <Field label="Ciudad" required id="f-ciudad">
                        <div className="relative">
                          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                          <Input
                            id="f-ciudad"
                            type="text"
                            value={company.ciudad}
                            onChange={e => setCompany(p => ({ ...p, ciudad: e.target.value }))}
                            onBlur={e => setCompany(p => ({ ...p, ciudad: collapseSpaces(e.target.value) }))}
                            placeholder="Ej: Mar del Plata"
                            maxLength={80}
                            disabled={submitting}
                            className="pl-10 rounded-xl"
                          />
                        </div>
                      </Field>
                    </div>

                    <Field label="Dirección" required id="f-direccion">
                      <div className="relative">
                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                        <Input
                          id="f-direccion"
                          type="text"
                          value={company.direccion}
                          onChange={e => setCompany(p => ({ ...p, direccion: e.target.value }))}
                          onBlur={e => setCompany(p => ({ ...p, direccion: collapseSpaces(e.target.value) }))}
                          placeholder="Ej: Av. Corrientes 1234, Piso 3"
                          maxLength={200}
                          disabled={submitting}
                          autoComplete="street-address"
                          className="pl-10 rounded-xl"
                        />
                      </div>
                    </Field>
                  </div>

                  {/* Bloque 4: Estructura */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-border">
                      <Users className="w-4 h-4 text-primary" aria-hidden="true" />
                      <h3 className="text-sm font-semibold text-foreground">Estructura de la empresa</h3>
                    </div>
                    <Field label="Cantidad de empleados" required id="f-empleados">
                      <div className="relative sm:max-w-xs">
                        <Select
                          value={company.empleados}
                          onValueChange={value => setCompany(p => ({ ...p, empleados: value }))}
                          disabled={submitting}
                        >
                          <SelectTrigger id="f-empleados" className="pl-10 rounded-xl">
                            <SelectValue placeholder="Seleccioná un rango" />
                          </SelectTrigger>
                          <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" aria-hidden="true" />
                          <SelectContent>
                            {EMPLEADOS_RANGOS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </Field>
                  </div>
                </div>
              )}

              {/* ── STEP 3 — Términos ─── */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Términos y condiciones</h2>
                    <p className="mt-1 text-muted-foreground">
                      Revisá el acuerdo de uso empresarial antes de finalizar el alta.
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    {[
                      { icon: Building2, color: 'indigo', title: 'Espacio de trabajo', desc: 'Se crea un entorno empresarial propio e independiente. Tu cuenta tendrá acceso total.' },
                      { icon: Shield, color: 'emerald', title: 'Privacidad de datos', desc: 'Tu información se gestiona con medidas de seguridad y se usa únicamente para operar el servicio.' },
                      { icon: Check, color: 'blue', title: 'Uso responsable', desc: 'Te comprometés a proteger tus credenciales y la información sensible de tu organización.' },
                    ].map(({ icon: Icon, color, title, desc }) => (
                      <div key={title} className="p-4 border rounded-xl border-border bg-card">
                        <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg mb-3
                          ${color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : ''}
                          ${color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : ''}
                          ${color === 'blue'    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : ''}
                        `}>
                          <Icon className="w-4 h-4" aria-hidden="true" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-foreground">{title}</h3>
                        <p className="text-xs leading-relaxed text-muted-foreground">{desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* Acuerdo + checkbox */}
                  <div className="p-4 space-y-4 border rounded-xl border-border bg-muted/20">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">Acuerdo de Servicio Empresarial</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Versión 1.0 — Vigente desde enero 2025</p>
                      </div>
                      <Button type="button" variant="outline" size="sm" className="flex-shrink-0" onClick={() => setAgreementOpen(true)}>
                        <FileText className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                        Leer acuerdo
                      </Button>
                    </div>

                    <div className="flex items-start gap-3 pt-3 border-t border-border">
                      <button
                        type="button"
                        id="terms-check"
                        role="checkbox"
                        aria-checked={termsChecked}
                        onClick={() => setTermsChecked(v => !v)}
                        disabled={submitting}
                        className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200
                          ${termsChecked ? 'bg-indigo-600 border-indigo-600' : 'border-border hover:border-indigo-400'}
                        `}
                      >
                        {termsChecked && <Check className="w-3 h-3 text-white" aria-hidden="true" />}
                      </button>
                      <label htmlFor="terms-check" className="text-sm leading-relaxed cursor-pointer select-none text-foreground">
                        Confirmo que leí y acepto el Acuerdo de Servicio Empresarial, la Política de Privacidad
                        y las responsabilidades de uso de la cuenta empresarial de PruneStone.
                      </label>
                    </div>
                  </div>

                  <AgreementModal open={agreementOpen} onOpenChange={setAgreementOpen} />

                  {/* Resumen del registro */}
                  <div className="overflow-hidden border rounded-xl border-border bg-card">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
                      <FileText className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                      <h3 className="text-sm font-semibold text-foreground">Resumen del registro</h3>
                    </div>
                    <div className="p-4 space-y-5">
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">Cuenta de administrador</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {[
                            { icon: User,  label: 'Nombre completo', value: account.nombre },
                            { icon: Mail,  label: 'Email de acceso',  value: account.email },
                          ].map(({ icon: Icon, label, value }) => (
                            <div key={label} className="flex items-start gap-2.5">
                              <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-muted flex items-center justify-center mt-0.5">
                                <Icon className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
                                <p className="text-sm font-medium truncate text-foreground">{value || '—'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-border" />

                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">Empresa</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {[
                            { icon: Building2, label: 'Nombre comercial',      value: company.nombreEmpresa },
                            { icon: Briefcase, label: 'Rubro',                 value: company.rubro },
                            { icon: Hash,      label: 'CUIT',                  value: company.cuit },
                            { icon: Users,     label: 'Cantidad de empleados', value: company.empleados },
                          ].map(({ icon: Icon, label, value }) => (
                            <div key={label} className="flex items-start gap-2.5">
                              <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-muted flex items-center justify-center mt-0.5">
                                <Icon className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
                                <p className="text-sm font-medium truncate text-foreground">{value || '—'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-border" />

                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">Ubicación</p>
                        <div className="flex items-start gap-2.5">
                          <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-muted flex items-center justify-center mt-0.5">
                            <MapPin className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] text-muted-foreground mb-0.5">Dirección completa</p>
                            <p className="text-sm font-medium text-foreground">
                              {[company.direccion, company.ciudad, company.provincia, company.pais].filter(Boolean).join(', ') || '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── STEP 4 — Verificación OTP ─── */}
              {step === 4 && (
                <div className="space-y-6">
                  <div className="space-y-2 text-center">
                    <div className="flex items-center justify-center mx-auto mb-4 bg-indigo-100 h-14 w-14 rounded-2xl dark:bg-indigo-900/30">
                      <Shield className="text-indigo-600 w-7 h-7 dark:text-indigo-400" aria-hidden="true" />
                    </div>
                    <h2 className="text-2xl font-bold text-foreground">Verificación de email</h2>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Enviamos un código de 6 dígitos a{' '}
                      <span className="font-semibold text-foreground">{account.email.trim().toLowerCase()}</span>
                    </p>
                  </div>

                  {/* Inputs OTP */}
                  <div className="flex justify-center gap-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <input
                        key={i}
                        id={`otp-${i}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={otpCode[i] ?? ''}
                        onChange={e => {
                          const char = e.target.value.replace(/\D/g, '').slice(-1);
                          const arr = otpCode.padEnd(6, '').split('');
                          arr[i] = char;
                          setOtpCode(arr.join('').slice(0, 6));
                          setOtpError('');
                          if (char) (document.getElementById(`otp-${i + 1}`) as HTMLInputElement | null)?.focus();
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Backspace') {
                            e.preventDefault();
                            const arr = otpCode.padEnd(6, '').split('');
                            arr[i] = '';
                            setOtpCode(arr.join('').slice(0, 6));
                            if (i > 0) (document.getElementById(`otp-${i - 1}`) as HTMLInputElement | null)?.focus();
                          }
                        }}
                        onPaste={e => {
                          const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                          if (p) { setOtpCode(p.padEnd(6, '').slice(0, 6)); (document.getElementById(`otp-5`) as HTMLInputElement | null)?.focus(); }
                          e.preventDefault();
                        }}
                        disabled={submitting}
                        className={`w-12 h-14 text-center text-xl font-bold rounded-xl border-2 bg-background text-foreground focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-40 transition-all duration-150 ${
                          otpCode[i] ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-border'
                        }`}
                      />
                    ))}
                  </div>

                  {otpError && (
                    <div className="flex items-center gap-2 px-3 py-2 border border-red-200 rounded-lg bg-red-50 dark:bg-red-950/30">
                      <AlertTriangle className="flex-shrink-0 w-4 h-4 text-red-500" aria-hidden="true" />
                      <p className="text-sm text-red-700 dark:text-red-300">{otpError}</p>
                    </div>
                  )}

                  <div className="space-y-1 text-center">
                    <p className="text-sm text-muted-foreground">¿No recibiste el código?</p>
                    <button
                      type="button"
                      onClick={handleOtpResend}
                      disabled={otpResendCooldown > 0 || otpResending}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {otpResending
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Enviando...</>
                        : otpResendCooldown > 0
                          ? `Reenviar en ${otpResendCooldown}s`
                          : <><RefreshCw className="w-3 h-3" /> Reenviar código</>}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-2 border rounded-lg border-amber-200 bg-amber-50 dark:bg-amber-950/30">
                    <Mail className="flex-shrink-0 w-4 h-4 text-amber-600" aria-hidden="true" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Revisá también la carpeta de spam. El código expira en 10 minutos.
                    </p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Footer navegación — no scrollea */}
        <div className="flex-shrink-0 px-4 py-4 border-t bg-background/95 backdrop-blur border-border sm:px-6">
          <div className="flex items-center justify-between max-w-2xl mx-auto">

            {step > 0 ? (
              <button
                type="button"
                onClick={() => goTo(step - 1)}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-xl hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Atrás
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-xl hover:bg-muted/50 transition-all duration-200"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Volver al login
              </Link>
            )}

            <span className="hidden text-xs text-muted-foreground sm:block" aria-live="polite">
              Paso {step + 1} de {STEPS.length}
            </span>

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() => step === 3 ? handleRequestOtp() : goTo(step + 1)}
                disabled={!canAdvance[step] || submitting}
                aria-disabled={!canAdvance[step] || submitting}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 focus:ring-offset-background"
              >
                {step === 3 ? (
                  submitting ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Enviando...</> : <>Verificar email <Mail className="w-4 h-4" aria-hidden="true" /></>
                ) : (
                  <>Continuar <ArrowRight className="w-4 h-4" aria-hidden="true" /></>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canAdvance[4] || submitting}
                aria-disabled={!canAdvance[4] || submitting}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 focus:ring-offset-background"
              >
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : <Check className="w-4 h-4" aria-hidden="true" />
                }
                {submitting ? 'Creando cuenta...' : 'Crear mi espacio de trabajo'}
              </button>
            )}
          </div>
        </div>

        {submitting && (
          <LoadingScreen message="Preparando tu espacio de trabajo y cuenta administradora..." />
        )}
      </div>

      {/* ── Modal de bienvenida al trial ─────────────────────────────── */}
      {showTrialModal && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Tu trial gratuito comienza ahora"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        >
          {/* Backdrop con blur */}
          <div
            className="absolute inset-0"
            style={{
              backdropFilter: 'blur(18px) saturate(0.65) brightness(0.7)',
              WebkitBackdropFilter: 'blur(18px) saturate(0.65) brightness(0.7)',
              backgroundColor: 'rgba(0,0,0,0.52)',
            }}
            aria-hidden="true"
          />

          {/* Card */}
          <div className="relative w-full max-w-md mx-auto">
            {/* Glow ambiental */}
            <div
              className="absolute pointer-events-none -inset-6 rounded-3xl opacity-20 blur-3xl"
              style={{ background: 'radial-gradient(ellipse at 50% 0%, #6366f1, transparent 70%)' }}
              aria-hidden="true"
            />

            <div className="relative overflow-hidden border shadow-2xl rounded-2xl border-white/10"
              style={{ background: 'linear-gradient(160deg, hsl(225,25%,12%) 0%, hsl(230,20%,9%) 100%)' }}
            >
              {/* Barra superior degradada */}
              <div className="w-full h-px" style={{ background: 'linear-gradient(90deg, transparent, #818cf8 40%, #a78bfa 60%, transparent)' }} />

              {/* Contenido */}
              <div className="px-8 pt-8 pb-7">

                {/* Ícono con sparkle */}
                <div className="flex items-center justify-center mb-6">
                  <div className="relative">
                    <div
                      className="flex items-center justify-center w-16 h-16 rounded-2xl"
                      style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(139,92,246,0.15))', border: '1px solid rgba(99,102,241,0.3)' }}
                    >
                      <Sparkles className="text-indigo-400 w-7 h-7" />
                    </div>
                    {/* Pulso */}
                    <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
                      <span className="absolute inline-flex w-full h-full bg-indigo-400 rounded-full opacity-50 animate-ping" />
                      <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-indigo-500" />
                    </span>
                  </div>
                </div>

                {/* Título */}
                <div className="mb-6 text-center">
                  <h2 className="mb-2 text-xl font-bold text-white">
                    ¡Tu espacio está listo! 🎉
                  </h2>
                  <p className="text-sm leading-relaxed text-slate-400">
                    Comenzás con <span className="font-semibold text-indigo-400">7 días de acceso completo</span> a todas las funcionalidades, sin necesidad de tarjeta de crédito.
                  </p>
                </div>

                {/* Ítems del trial */}
                <div className="mb-6 space-y-3">
                  {[
                    { icon: Clock,      text: '7 días de trial gratuito desde hoy' },
                    { icon: Check,      text: 'Acceso completo a todos los módulos' },
                    { icon: Users,      text: 'Podés invitar a tu equipo de inmediato' },
                    { icon: CreditCard, text: 'Al vencer, elegís un plan para continuar' },
                  ].map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-center gap-3">
                      <div
                        className="flex items-center justify-center rounded-lg w-7 h-7 shrink-0"
                        style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.2)' }}
                      >
                        <Icon className="w-3.5 h-3.5 text-indigo-400" />
                      </div>
                      <span className="text-sm text-slate-300">{text}</span>
                    </div>
                  ))}
                </div>

                {/* Aviso */}
                <div
                  className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl mb-6"
                  style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)' }}
                >
                  <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs leading-relaxed text-slate-400">
                    Cuando tu trial venza, el sistema te avisará y podrás elegir entre un plan <strong className="text-slate-300">mensual</strong> o <strong className="text-slate-300">anual</strong>. No se cobra nada automáticamente.
                  </p>
                </div>

                {/* CTA */}
                <button
                  id="trial-modal-confirm"
                  onClick={() => navigate('/login?registered=true')}
                  className="flex items-center justify-center w-full gap-2 px-6 text-sm font-semibold text-white transition-all duration-200 h-11 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #7c3aed)',
                    boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
                  }}
                >
                  Entendido, ir al inicio de sesión
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RegisterPage;
